import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import Modal from '../ui/Modal';
import { FieldGrid, toPayload, toFormState } from '../bids/FieldGrid';

export const iconBtn = 'inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50';
export const primaryBtn = 'inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50';
export const card = 'rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900';
export const opts = (m) => Object.entries(m).map(([value, label]) => ({ value, label }));
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

export const CHANNEL_LABEL = {
  linkedin_post: 'LinkedIn post', linkedin_newsletter: 'LinkedIn newsletter', email_newsletter: 'Email newsletter',
  email_campaign: 'Email campaign', blog_post: 'Website blog post', other: 'Other',
};
export const CONTENT_STATUS = { draft: 'Draft', approved: 'Approved', posted: 'Posted', archived: 'Archived' };
export const CONTENT_TONE = { draft: 'amber', approved: 'blue', posted: 'green', archived: 'gray' };
export const EVENT_KIND = {
  trade_show: 'Trade show', conference: 'Conference', training: 'Training', sponsorship: 'Sponsorship',
  demo: 'Demo', webinar: 'Webinar', meeting: 'Meeting', other: 'Other',
};
export const EVENT_STATUS = {
  considering: 'Considering', planned: 'Planned', registered: 'Registered', attended: 'Attended',
  skipped: 'Skipped', cancelled: 'Cancelled', unknown: 'Not recorded',
};
export const EVENT_TONE = { considering: 'gray', planned: 'blue', registered: 'indigo', attended: 'green', skipped: 'gray', cancelled: 'red', unknown: 'amber' };
export const REIMB = { not_applicable: 'Not applicable', to_invoice: 'To invoice Concria', invoiced: 'Invoiced', offset: 'Offset against balance', paid: 'Paid' };
export const SOURCE_LABEL = {
  contact_form: 'Contact form', 'datasheet-download': 'Data sheet request', chatbot: 'Chatbot', website: 'Website',
  portal_manual: 'Entered in portal', trade_show: 'Event', unknown: 'Unknown',
};
export const sourceLabel = (s) => SOURCE_LABEL[s] || s;

const ACTION_LABEL = {
  create: 'Created', import: 'Imported', update: 'Edited', delete: 'Deleted', restore: 'Restored', approve: 'Approved',
  post: 'Marked posted', reopen: 'Back to draft', publish: 'Published', unpublish: 'Unpublished', link: 'Linked to CRM',
  reject: 'Rejected', export: 'Exported', remove: 'Removed',
};

export function useList(path, key) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const seq = useRef(0);
  // The view on screen now: an action's reload loads this view, never the one it started under.
  const shownPath = useRef(path);
  shownPath.current = path;
  const load = useCallback(async () => {
    // Only the latest request's response may update the list.
    const mine = ++seq.current;
    try { const r = await api.get(shownPath.current); if (mine === seq.current) { setRows(key ? r[key] : r); setErr(''); } } catch (e) { if (mine === seq.current) setErr(e.message); }
  }, [path, key]);
  useEffect(() => { load(); }, [load]);
  return { rows, err, load, setErr };
}

export function FormModal({ title, fields, record, onSave, onClose, children }) {
  const [value, setValue] = useState(() => toFormState(fields, record || {}));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    const { payload, error } = toPayload(fields, value, record?.id ? record : null);
    if (error) { setErr(error); return; }
    if (record?.id && !Object.keys(payload).length) { onClose(); return; }
    setBusy(true);
    try { await onSave(payload); } catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <Modal title={title} onClose={onClose} maxWidth="max-w-3xl">
      {children}
      <FieldGrid fields={fields} value={value} onChange={setValue} />
      {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
        <button disabled={busy} onClick={save} className={primaryBtn}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

export function HistoryModal({ table, id, title, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.get(`/marketing/history/${table}/${id}`).then((r) => setRows(r.history)).catch((e) => setErr(e.message)); }, [table, id]);
  const show = (o) => (o ? Object.entries(o).filter(([k]) => !['id', 'created_by', 'deleted_by', 'content'].includes(k))
    .map(([k, v]) => `${k}: ${v == null ? 'blank' : typeof v === 'object' ? JSON.stringify(v) : String(v).slice(0, 200)}`).join('; ') : '');
  return (
    <Modal title={`History: ${title}`} onClose={onClose}>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      <ul className="space-y-2 text-sm">
        {(rows || []).map((h) => (
          <li key={h.id} className="rounded-lg border border-[var(--border)] p-2">
            <div className="text-xs text-[var(--text-secondary)]">{fmtDateTime(h.created_at)} · {h.actor || 'system'} · {ACTION_LABEL[h.action] || h.action}</div>
            {!['create', 'import'].includes(h.action) && h.after && <div className="mt-1 break-words text-xs">{show(h.after)}</div>}
          </li>
        ))}
        {rows && !rows.length && <li className="text-[var(--text-secondary)]">No history yet.</li>}
      </ul>
    </Modal>
  );
}

export function Empty({ children }) {
  return <p className="rounded-lg border border-dashed border-[var(--border)] p-4 text-center text-sm text-[var(--text-secondary)]">{children}</p>;
}
